import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { generatePastelColor, generateSaturatedColor } from '../Utils/colorUtils';
import CircularProgress from '@mui/material/CircularProgress';
import { useWebSocket } from './WebSocketContext';
import TimeEventProof from './Timevents/TimeEventProof';

const VALUE_PREVIEW_LENGTH = 140;

const TagColorChip = ({ label }) => {
  if (!label) {
    return null;
  }

  const accentColor = generateSaturatedColor(label);
  const pastelColor = generatePastelColor(label);

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.5,
        py: 0.45,
        borderRadius: '999px',
        border: `1px solid ${alpha(accentColor, 0.6)}`,
        backgroundColor: pastelColor,
        color: accentColor,
        fontWeight: 600,
        boxShadow: `0 6px 16px ${alpha(accentColor, 0.2)}`,
        fontSize: '0.85rem',
        textTransform: 'none'
      }}
    >
      <Typography variant="body2" component="span" sx={{ lineHeight: 1 }}>
        {label}
      </Typography>
    </Box>
  );
};

const normalizeTags = (raw) => {
  if (!raw) {
    return [];
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed;
      }
      if (parsed && typeof parsed === 'object') {
        return [parsed];
      }
      return [];
    } catch (error) {
      return [];
    }
  }

  if (Array.isArray(raw)) {
    return raw;
  }

  if (typeof raw === 'object') {
    return [raw];
  }

  return [];
};

const SettingProjectTags = ({ caseid, active }) => {
  const { t } = useTranslation();
  const [expandedTagId, setExpandedTagId] = useState(null);
  const [proofDialogOpen, setProofDialogOpen] = useState(false);
  const [selectedProofTag, setSelectedProofTag] = useState(null);
  const [projectTags, setProjectTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestedCaseRef = useRef(null);
  const { ws, subscribeToMessages, isConnected } = useWebSocket();
  const navigate = useNavigate();

  useEffect(() => {
    setProjectTags([]);
    setError('');
    setLoading(false);
    requestedCaseRef.current = null;
  }, [caseid]);

  useEffect(() => {
    const isSocketOpen = ws && ws.readyState === 1;
    if (!active || !caseid || !ws || !isConnected || !isSocketOpen) {
      return;
    }

    if (requestedCaseRef.current === caseid) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      ws.send(JSON.stringify({ command: 'get_project_tags', caseid }));
      requestedCaseRef.current = caseid;
    } catch (sendError) {
      console.error('project_tags request failed', sendError);
      setError(t('settings.project_tags.request_error', 'Unable to load project tags'));
      setLoading(false);
      requestedCaseRef.current = null;
    }
  }, [active, caseid, ws, isConnected, t]);

  useEffect(() => {
    if (!subscribeToMessages) {
      return () => {};
    }

    const handler = (message) => {
      if (message.command !== 'project_tags') {
        return;
      }
      const data = message.data || {};
      if (!data.caseid || String(data.caseid) !== String(caseid)) {
        return;
      }

      if (data.error) {
        setError(data.error);
        setLoading(false);
        return;
      }

      setProjectTags(Array.isArray(data.tags) ? data.tags : []);
      setLoading(false);
    };

    const unsubscribe = subscribeToMessages(['project_tags'], handler);
    return unsubscribe;
  }, [caseid, subscribeToMessages]);

  const normalizedTags = useMemo(() => {
    return normalizeTags(projectTags)
      .map((tag, index) => {
        if (!tag || typeof tag !== 'object') {
          return null;
        }
        const variable = String(tag.variable || tag.name || (`tag-${index + 1}`)).trim();
        if (!variable) {
          return null;
        }
        const rawResponse = tag.response ?? tag.reply ?? tag.value ?? '';
        let responseValue = '';
        if (typeof rawResponse === 'string') {
          responseValue = rawResponse;
        } else if (rawResponse != null) {
          try {
            responseValue = JSON.stringify(rawResponse);
          } catch (error) {
            responseValue = String(rawResponse);
          }
        }
        return {
          id: tag.id ?? `${variable}-${index}`,
          variable,
          description: tag.description ?? '',
          response: responseValue,
          type: tag.type ?? 'text',
          tag_type: tag.tag_type ?? '',
          tag_typeid: tag.tag_typeid ?? null,
        };
    })
    .filter(Boolean);
  }, [projectTags]);

  const displayTags = useMemo(() => {
    const filtered = normalizedTags.filter((tag) => (tag.tag_type || '').toLowerCase() !== 'timeevent');
    return filtered.sort((a, b) => a.variable.localeCompare(b.variable));
  }, [normalizedTags]);

  const handleToggleExpand = (tagId) => {
    setExpandedTagId((prev) => (prev === tagId ? null : tagId));
  };

  const handleOpenWidget = useCallback(
    (tag) => {
      if (!caseid) {
        return;
      }
      const params = new URLSearchParams();
      params.set('openWidgetSettings', 'true');
      if (tag?.variable) {
        params.set('widgetTag', tag.variable);
      }
      const caseIdParam = encodeURIComponent(String(caseid));
      navigate(`/case/${caseIdParam}?${params.toString()}`);
    },
    [caseid, navigate]
  );

  const hasValidProofTagId = useCallback((tag) => {
    const parsedId = Number(tag?.id);
    return Number.isFinite(parsedId);
  }, []);

  const handleOpenProof = useCallback((tag) => {
    if (!hasValidProofTagId(tag)) {
      return;
    }

    setSelectedProofTag({
      id: tag.id,
      variable: tag.variable,
      description: tag.description,
      reply: tag.response,
      tag_type: tag.tag_type,
      tag_typeid: tag.tag_typeid
    });
    setProofDialogOpen(true);
  }, [hasValidProofTagId]);

  const handleCloseProof = useCallback(() => {
    setProofDialogOpen(false);
    setSelectedProofTag(null);
  }, []);

  const renderResponseCell = (tag) => {
    const isWidgetTag = (tag.tag_type || '').toLowerCase() === 'widget';
    if (isWidgetTag) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Button
            size="small"
            variant="text"
            onClick={() => handleOpenWidget(tag)}
            disabled={!caseid}
            sx={{
              p: 0,
              textTransform: 'none',
              alignSelf: 'flex-start',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: '#5757b0'
            }}
          >
            {t('settings.project_tags.actions.open_widget', 'Open widget')}
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={() => handleOpenProof(tag)}
            disabled={!hasValidProofTagId(tag)}
            sx={{
              p: 0,
              textTransform: 'none',
              alignSelf: 'flex-start',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: '#5757b0'
            }}
          >
            {t('settings.project_tags.actions.view_proof', 'View proof')}
          </Button>
        </Box>
      );
    }

    const value = tag.response || '';
    const isLong = value.length > VALUE_PREVIEW_LENGTH;
    const isExpanded = expandedTagId === tag.id;
    const displayValue = value
      ? (isLong && !isExpanded ? `${value.slice(0, VALUE_PREVIEW_LENGTH)}...` : value)
      : t('settings.project_tags.empty_response', 'No value provided');

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: '0.85rem'
          }}
        >
          {displayValue}
        </Typography>
        {isLong && (
          <Button
            size="small"
            variant="text"
            onClick={() => handleToggleExpand(tag.id)}
            sx={{
              p: 0,
              textTransform: 'none',
              alignSelf: 'flex-start',
              fontSize: '0.75rem',
              fontWeight: 600
            }}
          >
            {isExpanded
              ? t('settings.project_tags.show_less', 'Show less')
              : t('settings.project_tags.show_more', 'Show more')}
          </Button>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ pt: 3, textAlign: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ pt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!projectTags.length) {
    return (
      <Box sx={{ pt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('settings.project_tags.empty', 'No project tags available yet.')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 6,
          borderRadius: 3,
          overflow: 'hidden'
        }}
      >
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: '#3c3c6f' }}>{t('settings.project_tags.headers.name', 'Name')}</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#3c3c6f' }}>{t('settings.project_tags.headers.description', 'Description')}</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#3c3c6f' }}>{t('settings.project_tags.headers.response', 'Response')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayTags.map((tag) => (
                <TableRow key={tag.id} hover>
                <TableCell
                  sx={{
                    py: 1.5,
                    minWidth: { xs: 120, sm: 140 },
                    width: { xs: 120, sm: 160 },
                    maxWidth: 170,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start'
                  }}
                >
                  <TagColorChip label={tag.variable} />
                </TableCell>
                <TableCell
                  sx={{
                    py: 1.5,
                    width: { xs: '35%', md: '34%' },
                    minWidth: 180
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {tag.description || t('settings.project_tags.no_description', 'No description')}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    py: 1.5,
                    width: { xs: '45%', md: '50%' }
                  }}
                >
                  {renderResponseCell(tag)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TimeEventProof
        open={proofDialogOpen}
        onClose={handleCloseProof}
        timeEvent={null}
        field={selectedProofTag}
      />
    </Box>
  );
};

export default SettingProjectTags;
